import React, { useState, useEffect } from 'react';
import './VehicleTracking.scss';

const VehicleTracking: React.FC = () => {
  const [activeTab, setActiveTab] = useState('live-location');
  const [isVisible, setIsVisible] = useState(false);
  const [animateScreenshot, setAnimateScreenshot] = useState(false);
  const [modalImage, setModalImage] = useState<string | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 200);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    setAnimateScreenshot(true);
    const timer = setTimeout(() => setAnimateScreenshot(false), 600);
    return () => clearTimeout(timer);
  }, [activeTab]);

  const tracFeatures = [
    {
      id: 'live-location',
      title: 'Live Location Tracking',
      description: 'Real-time GPS tracking with precise location updates',
      image: '/images/karma-live-location.png',
      features: ['Real-time GPS updates', 'Street-level accuracy', 'Live vehicle status', 'Current location details'],
      icon: '📍',
      color: '#00ff88'
    },
    {
      id: 'trip-routes',
      title: 'Trip Route Analysis',
      description: 'Detailed trip visualization with speed and route data',
      image: '/images/karma-trip-route.png',
      features: ['Complete trip routes', 'Speed monitoring', 'Route optimization', 'Trip duration tracking'],
      icon: '🛣️',
      color: '#ff6b35'
    },
    {
      id: 'reports',
      title: 'Comprehensive Reports',
      description: 'Detailed analytics and reporting dashboard',
      image: '/images/karma-reports.png',
      features: ['Distance reports', 'Cost analysis', 'Trip history', 'Performance metrics'],
      icon: '📊',
      color: '#7c3aed'
    },
    {
      id: 'vehicle-status',
      title: 'Vehicle Management',
      description: 'Complete vehicle status and information tracking',
      image: '/images/365-trac-vehicle-management.png',
      features: ['Vehicle details', 'Driver information', 'Status monitoring', 'Fleet overview'],
      icon: '🚗',
      color: '#00d4ff'
    }
  ];

  const activeFeature = tracFeatures.find(feature => feature.id === activeTab) || tracFeatures[0];

  const handlePurchaseDevice = () => {
    window.open('https://shadow-16723594.myshopify.com/products/starlink-tracker', '_blank');
  };

  const handlePurchaseSubscription = () => {
    window.open('https://shadow-16723594.myshopify.com/products/365-trac-subscription', '_blank');
  };

  return (
    <section className={`vehicle-tracking ${isVisible ? 'visible' : ''}`}>
      <div className="floating-particles">
        {[...Array(6)].map((_, i) => (
          <div key={i} className={`particle particle-${i + 1}`}></div>
        ))}
      </div>

      <div className="container">
        <div className="tracking-header">
          <div className="header-badge">
            <span className="badge-pulse"></span>
            PROFESSIONAL TRACKING
          </div>
          <h2>365 Trac Fleet Management Platform</h2>
          <p>Experience the future of fleet tracking with our advanced 365 Trac platform</p>
          <div className="header-stats">
            <div className="stat">
              <div className="stat-number">99.9%</div>
              <div className="stat-label">Uptime</div>
            </div>
            <div className="stat">
              <div className="stat-number">24/7</div>
              <div className="stat-label">Monitoring</div>
            </div>
            <div className="stat">
              <div className="stat-number">Real-time</div>
              <div className="stat-label">Updates</div>
            </div>
          </div>
        </div>

        <div className="trac-showcase">
          <div className="showcase-glow"></div>
          
          <div className="feature-tabs">
            {tracFeatures.map((feature, index) => (
              <button
                key={feature.id}
                className={`tab-button ${activeTab === feature.id ? 'active' : ''}`}
                onClick={() => setActiveTab(feature.id)}
                style={{ '--delay': `${index * 0.1}s`, '--accent-color': feature.color } as React.CSSProperties}
              >
                <div className="tab-icon" style={{ color: feature.color }}>
                  {feature.icon}
                </div>
                <div className="tab-content">
                  <h3>{feature.title}</h3>
                  <p>{feature.description}</p>
                </div>
                <div className="tab-indicator"></div>
              </button>
            ))}
          </div>

          <div className="feature-display">
            <div className="app-preview">
              <div className="preview-glow" style={{ '--glow-color': activeFeature.color } as React.CSSProperties}></div>
              <div className="browser-frame">
                <div className="browser-header">
                  <div className="browser-controls">
                    <div className="control red"></div>
                    <div className="control yellow"></div>
                    <div className="control green"></div>
                  </div>
                  <div className="browser-title">
                    <span className="trac-logo">⚡</span>
                    365 Trac Fleet Management
                    <div className="live-indicator">
                      <span className="live-dot"></span>
                      LIVE
                    </div>
                  </div>
                </div>
                <div className="browser-content">
                  <img 
                    src={activeFeature.image} 
                    alt={activeFeature.title}
                    className={`trac-screenshot ${animateScreenshot ? 'animate' : ''}`}
                    onClick={() => setModalImage(activeFeature.image)}
                    style={{ cursor: 'pointer' }}
                  />
                  <div className="screenshot-overlay">
                    <div className="click-hint">
                      <span>🔍 Click to enlarge</span>
                    </div>
                    <div className="data-points">
                      <div className="data-point" style={{ top: '20%', left: '15%' }}>
                        <div className="point-pulse"></div>
                      </div>
                      <div className="data-point" style={{ top: '60%', right: '20%' }}>
                        <div className="point-pulse"></div>
                      </div>
                      <div className="data-point" style={{ bottom: '25%', left: '30%' }}>
                        <div className="point-pulse"></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="feature-details">
              <div className="feature-info">
                <div className="feature-header">
                  <div className="feature-icon-large" style={{ color: activeFeature.color }}>
                    {activeFeature.icon}
                  </div>
                  <h3>{activeFeature.title}</h3>
                </div>
                <p className="feature-description">{activeFeature.description}</p>
                
                <div className="feature-list">
                  <h4>Key Features:</h4>
                  <ul>
                    {activeFeature.features.map((feature, index) => (
                      <li key={index} style={{ '--delay': `${index * 0.1}s` } as React.CSSProperties}>
                        <span className="check-icon">✓</span>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="trac-badge">
                  <div className="badge-icon">🚗</div>
                  <div className="badge-content">
                    <div className="badge-title">365 Trac Platform</div>
                    <div className="badge-subtitle">Professional Fleet Management</div>
                  </div>
                  <div className="badge-pulse"></div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="trac-benefits">
          {[
            { icon: '📍', title: 'Real-Time Tracking', desc: 'Monitor your fleet with precise GPS tracking and live location updates', color: '#00ff88' },
            { icon: '📊', title: 'Detailed Analytics', desc: 'Comprehensive reports on distance, costs, and vehicle performance', color: '#7c3aed' },
            { icon: '🛣️', title: 'Route Optimization', desc: 'Analyze trip routes and optimize for efficiency and cost savings', color: '#ff6b35' },
            { icon: '👥', title: 'Fleet Management', desc: 'Manage multiple vehicles and drivers from a single dashboard', color: '#00d4ff' }
          ].map((benefit, index) => (
            <div 
              key={index} 
              className="benefit-card"
              style={{ '--delay': `${index * 0.15}s`, '--accent-color': benefit.color } as React.CSSProperties}
            >
              <div className="benefit-icon" style={{ color: benefit.color }}>
                {benefit.icon}
              </div>
              <h4>{benefit.title}</h4>
              <p>{benefit.desc}</p>
              <div className="card-glow" style={{ '--glow-color': benefit.color } as React.CSSProperties}></div>
            </div>
          ))}
        </div>

        <div className="starlink-pricing">
          <div className="pricing-header">
            <h2>Get Started with Starlink Tracking</h2>
            <p>Professional fleet management for your business</p>
          </div>
          
          <div className="pricing-grid">
            <div className="price-card device-card">
              <div className="card-header">
                <h3>Starlink Tracker Device</h3>
                <div className="price">$299.95</div>
                <p className="price-subtitle">Professional Hardware:</p>
              </div>
              <ul className="features-list">
                <li><span className="check">✓</span>Hardwired installation</li>
                <li><span className="check">✓</span>Tamper detection</li>
                <li><span className="check">✓</span>Real-time GPS tracking</li>
                <li><span className="check">✓</span>Fleet management ready</li>
                <li><span className="check">✓</span>Professional installation</li>
                <li><span className="check">✓</span>2-year warranty</li>
              </ul>
              <button className="purchase-btn device-btn" onClick={handlePurchaseDevice}>
                Order Starlink Device
              </button>
            </div>

            <div className="price-card subscription-card featured">
              <div className="featured-badge">RECOMMENDED</div>
              <div className="card-header">
                <h3>365 Trac Platform</h3>
                <div className="price">$29.95<span>/month</span></div>
                <p className="price-subtitle">Complete Fleet Management:</p>
              </div>
              <ul className="features-list">
                <li><span className="check">✓</span>Live location tracking</li>
                <li><span className="check">✓</span>Trip route analysis</li>
                <li><span className="check">✓</span>Comprehensive reports</li>
                <li><span className="check">✓</span>Vehicle management</li>
                <li><span className="check">✓</span>24/7 monitoring</li>
                <li><span className="check">✓</span>Mobile app access</li>
                <li><span className="check">✓</span>Cancel anytime</li>
              </ul>
              <button className="purchase-btn subscription-btn" onClick={handlePurchaseSubscription}>
                Start 365 Trac Subscription
              </button>
            </div>
          </div>

          <div className="pricing-note">
            <p>*Both Starlink device and 365 Trac subscription required for full functionality</p>
          </div>
        </div>
      </div>

      {/* Image Modal */}
      {modalImage && (
        <div className="image-modal" onClick={() => setModalImage(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setModalImage(null)}>
              ✕
            </button>
            <img src={modalImage} alt="Enlarged view" className="modal-image" />
          </div>
        </div>
      )}
    </section>
  );
};

export default VehicleTracking;
